import { Chord } from "tonal";
import { midiToTonalNote, tonalNoteToMidi } from "./notes";
import { ScaleType } from "./scales";

export type ChordType = "major" | "minor" | "aug" | "dim";

interface ScaleChords {
  type: ScaleType;
  degreeToChord: Map<number, ChordType>;
}

const majorScaleChords: ScaleChords = {
  type: ScaleType.Major,
  degreeToChord: new Map([
    [1, "major"],
    [2, "minor"],
    [3, "minor"],
    [4, "major"],
    [5, "major"],
    [6, "minor"],
    [7, "dim"],
    [8, "major"],
  ]),
};

const minorScaleChords: ScaleChords = {
  type: ScaleType.Minor,
  degreeToChord: new Map([
    [1, "minor"],
    [2, "dim"],
    [3, "major"],
    [4, "minor"],
    [5, "minor"],
    [6, "major"],
    [7, "major"],
    [8, "minor"],
  ]),
};

export function getChordByDegree(
  scaleType: ScaleType,
  degree: number
): ChordType {
  const scaleChords = getScaleChords(scaleType);
  const chord = scaleChords.degreeToChord.get(degree);

  if (!chord) {
    throw new Error(
      `Unsupported degree ${degree} for ${scaleChords.type} scale`
    );
  }

  return chord;
}

export function getChordMidiNotes(
  rootMidi: number,
  chordType: ChordType
): number[] {
  return Chord.notes(chordType, midiToTonalNote(rootMidi)).map(tonalNoteToMidi);
}

export function getScaleChords(scale: ScaleType): ScaleChords {
  if (scale === "major") {
    return majorScaleChords;
  }

  if (scale === "minor") {
    return minorScaleChords;
  }

  throw new Error("Unsupported scale type");
}

export function getScaleDegreesOfChord(): number[] {
  return Array.from(majorScaleChords.degreeToChord.keys());
}

// TODO: need to verify these are all correct - i think they are?
export function getChordScaleDegrees(
  scaleType: ScaleType,
  degree: number
): number[] {
  if (scaleType === ScaleType.Major) {
    if (degree <= 0) {
      throw new Error(`Invalid major degree ${degree}`);
    }

    if (degree === 1) {
      return [1, 3, 5];
    }

    if (degree === 2) {
      return [2, 4, 6];
    }

    if (degree === 3) {
      return [3, 5, 7];
    }

    if (degree === 4) {
      return [4, 6, 1];
    }

    if (degree === 5) {
      return [5, 7, 2];
    }

    if (degree === 6) {
      return [6, 1, 3];
    }

    if (degree === 7) {
      return [7, 2, 4];
    }

    throw new Error(`Invalid major degree ${degree}`);
  }

  if (scaleType === ScaleType.Minor) {
    if (degree <= 0) {
      throw new Error(`Invalid minor degree ${degree}`);
    }

    if (degree === 1) {
      return [1, 3, 5];
    }

    if (degree === 2) {
      return [2, 4, 6];
    }

    if (degree === 3) {
      return [3, 5, 7];
    }

    if (degree === 4) {
      return [4, 6, 1];
    }

    if (degree === 5) {
      return [5, 7, 2];
    }

    if (degree === 6) {
      return [6, 1, 3];
    }

    if (degree === 7) {
      return [7, 2, 4];
    }

    throw new Error(`Invalid minor degree ${degree}`);
  }

  throw new Error(`Unsupported scale type ${scaleType}`);
}

export function getChordTypeShortname(chordType: ChordType): string {
  if (chordType === "major") {
    return "maj";
  }

  if (chordType === "minor") {
    return "min";
  }

  if (chordType === "aug") {
    return "aug";
  }

  if (chordType === "dim") {
    return "dim";
  }

  throw new Error(`Unsupported chord type ${chordType}`);
}
